import axios from 'axios';
import moment from 'moment';
import { useState } from 'react';

import { Check, Clear, Delete, Edit } from '@mui/icons-material';
import { Box, Button, Input, Typography, useTheme } from '@mui/material';

import TextBlock from '../../Components/Layout/TextBlock';
import ListItem from '../../Components/ListItem';
import ListItemAction from '../../Components/ListItemAction';
import ListItemActions from '../../Components/ListItemActions';
import ListItemContent from '../../Components/ListItemContent';
import dateFormats from '../../system/dateFormats';
import { SaleDealerInformationItem, ThemeColor, Vehicle } from '../../system/Domain';

type NewProps = {
	item: Partial<SaleDealerInformationItem>;
	new: true;
	cancel: () => void;
};

type EditProps = {
	item: SaleDealerInformationItem;
	new?: false;
};

interface CommonProps {
	vehicle: Vehicle;
	save: (vehicle: Vehicle) => void;
	disabled?: boolean;
}

type SpecificProps = NewProps | EditProps;

type Mode = 'view' | 'edit' | 'delete';

const SaleDealerInformationListItem = (props: CommonProps & SpecificProps) => {
	const [de, setDe] = useState<string>(props.item.de ?? '');
	const [en, setEn] = useState<string>(props.item.en ?? '');
	const [mode, setMode] = useState<Mode>(props.new ? 'edit' : 'view');
	const [saving, setSaving] = useState(false);
	const theme = useTheme();

	const canSave = de.trim().length > 0 || en.trim().length > 0;

	const save = async () => {
		if (!canSave || saving) return;

		setSaving(true);
		try {
			if (props.new) {
				const { data: updatedVehicle } = await axios.post<Vehicle>(
					`/api/vehicles/${props.vehicle.id}/remarketing/dealer-information`,
					{ de, en }
				);
				props.save(updatedVehicle);
			} else {
				const { data: updatedVehicle } = await axios.put<Vehicle>(
					`/api/vehicles/${props.vehicle.id}/remarketing/dealer-information/${props.item.id}`,
					{ de, en }
				);
				setMode('view');
				props.save(updatedVehicle);
			}
		} finally {
			setSaving(false);
		}
	};

	const remove = async () => {
		if (!props.new) {
			const { data: updatedVehicle } = await axios.delete<Vehicle>(
				`/api/vehicles/${props.vehicle.id}/remarketing/dealer-information/${props.item.id}`
			);
			props.save(updatedVehicle);
		}
	};

	const startEditing = () => {
		setDe(props.item.de ?? '');
		setEn(props.item.en ?? '');
		setMode('edit');
	};

	const stopEditing = () => {
		setMode('view');
		if (props.new) {
			props.cancel();
		}
	};

	let color: ThemeColor = 'info';
	if (mode === 'edit') color = 'primary';
	if (mode === 'delete') color = 'error';

	return (
		<ListItem color={color}>
			{!props.disabled && (
				<ListItemActions color={color} disabled={saving}>
					{mode === 'view' && (
						<>
							<ListItemAction icon={<Edit />} onClick={startEditing} />
							<ListItemAction icon={<Delete />} onClick={() => setMode('delete')} />
						</>
					)}
					{mode === 'edit' && (
						<>
							<ListItemAction
								icon={<Check />}
								disabled={!canSave}
								onClick={save}
							/>
							<ListItemAction
								icon={<Clear />}
								onClick={stopEditing}
							/>
						</>
					)}
					{mode === 'delete' && (
						<ListItemAction icon={<Clear />} onClick={() => setMode('view')} />
					)}
				</ListItemActions>
			)}
			<ListItemContent color={color}>
				{mode === 'edit' && (
					<>
						<Box mb={2}>
							<Typography variant="caption" color="textSecondary">Deutsch</Typography>
							<Input
								fullWidth
								autoFocus
								multiline
								minRows={2}
								value={de}
								onChange={e => setDe(e.target.value)}
							/>
						</Box>
						<Box>
							<Typography variant="caption" color="textSecondary">Englisch</Typography>
							<Input
								fullWidth
								multiline
								minRows={2}
								value={en}
								onChange={e => setEn(e.target.value)}
							/>
						</Box>
					</>
				)}
				{mode !== 'edit' && !props.new && (
					<>
						{props.item.de && (
							<Box mb={props.item.en ? 1 : 0}>
								<TextBlock
									primary={props.item.de}
									secondary="Deutsch"
								/>
							</Box>
						)}
						{props.item.en && (
							<Box>
								<TextBlock
									primary={props.item.en}
									secondary="English"
								/>
							</Box>
						)}
						<Typography variant="caption" color="textSecondary">
							{`${props.item.createdBy?.name} – ${moment(props.item.dateCreated).format(dateFormats.dateTime)}`}
						</Typography>
						{mode === 'delete' && (
							<Box mt={3} color={theme.palette.error.light}>
								<Button
									color="inherit"
									variant="text"
									onClick={remove}
									startIcon={<Delete />}
								>
									Händlerinformation entfernen
								</Button>
							</Box>
						)}
					</>
				)}
			</ListItemContent>
		</ListItem>
	);
};

export default SaleDealerInformationListItem;
